import {
  getInt32,
  getObject,
  getString,
  getOptionalString,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { TypesSortOrder } from "../types/typesSortOrder";
import { EmailTemplate, EmailTemplateDTO } from "./emailTemplate";
/* eslint-disable import/export */
export abstract class GetEmailTemplatesRPC implements
  LeoRPC<GetEmailTemplatesRPC.Request, GetEmailTemplatesRPC.Response, GetEmailTemplatesRPC.Errors.Errors>
{
  abstract execute(
    request: GetEmailTemplatesRPC.Request
  ): Promise<LeoRPCResult<GetEmailTemplatesRPC.Response, GetEmailTemplatesRPC.Errors.Errors>>;
}

export namespace GetEmailTemplatesRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace SortColumn {
      export enum SortColumn {
        TEMPLATE_NAME = "TEMPLATE_NAME",
        MODIFIED_AT = "MODIFIED_AT",
      }
    
      export interface SortColumnDTO {
        case: string;
      }
    
      export const fromDTO = (dto: SortColumnDTO): SortColumn => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestSortColumnError(
            `Invalid DTO ${dto} provided for SortColumn.SortColumn`
          );
        }
        switch (dto.case) {
          case "TEMPLATE_NAME":
            return SortColumn.TEMPLATE_NAME;
          case "MODIFIED_AT":
            return SortColumn.MODIFIED_AT;
          default:
            throw new InvalidRequestSortColumnError(
              `Case ${dto.case} is not valid case of SortColumn.SortColumn`
            );
        }
      };
    
      export const toDTO = (sortColumn: SortColumn): SortColumnDTO => {
        const enumDTO = {
          case: SortColumn[sortColumn],
        };
        return enumDTO;
      };
    }
    
  }
  export interface RequestDTO {
    pageIndex: number;
    itemsPerPage: number;
    sortOrder: TypesSortOrder.TypesSortOrderDTO;
    sortColumn: RequestEnums.SortColumn.SortColumnDTO;
    searchQuery?: string | null;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetEmailTemplatesRPCRequest: boolean = true;
  
    readonly pageIndex: number;
    readonly itemsPerPage: number;
    readonly sortOrder: TypesSortOrder.TypesSortOrder;
    readonly sortColumn: RequestEnums.SortColumn.SortColumn;
    readonly searchQuery: string | null;
  
    static readonly constraints = {
      "pageIndex": {
        "minValue": 0,
      },
      "itemsPerPage": {
        "minValue": 10,
      },
    }
  
    constructor(
    pageIndex: number, 
    itemsPerPage: number, 
    sortOrder: TypesSortOrder.TypesSortOrder, 
    sortColumn: RequestEnums.SortColumn.SortColumn, 
    searchQuery: string | null = null
    ) {
      Request.validatePageIndex(pageIndex);
      this.pageIndex = pageIndex;
      Request.validateItemsPerPage(itemsPerPage);
      this.itemsPerPage = itemsPerPage;
      this.sortOrder = sortOrder;
      this.sortColumn = sortColumn;
      if ( searchQuery !== undefined && searchQuery !== null) {
        Request.validateSearchQuery(searchQuery);
      }
      this.searchQuery = searchQuery;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const pageIndex = getInt32(dto, "pageIndex", InvalidRequestPageIndexError);
      const itemsPerPage = getInt32(dto, "itemsPerPage", InvalidRequestItemsPerPageError);
      const sortOrder = TypesSortOrder.fromDTO(getObject(dto, "sortOrder", InvalidRequestSortOrderError));
      const sortColumn = RequestEnums.SortColumn.fromDTO(getObject(dto, "sortColumn", InvalidRequestSortColumnError));
      const searchQuery = getOptionalString(dto, "searchQuery", InvalidRequestSearchQueryError);
      return new Request(
        pageIndex,
        itemsPerPage,
        sortOrder,
        sortColumn,
        searchQuery
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          pageIndex: this.pageIndex,
          itemsPerPage: this.itemsPerPage,
          sortOrder: TypesSortOrder.toDTO(this.sortOrder),
          sortColumn: RequestEnums.SortColumn.toDTO(this.sortColumn),
      };
      if (this.searchQuery !== undefined && this.searchQuery !== null){
        dto.searchQuery = this.searchQuery;
      }
      return dto;
    }
    private static validatePageIndex = (pageIndex: number): void => {
      if(pageIndex < 0) {
        throw new InvalidRequestPageIndexMinValueError(
          0,
          `Attribute pageIndex has value ${pageIndex}. Min value is 0.`
        );
      }
    }
    private static validateItemsPerPage = (itemsPerPage: number): void => {
      if(itemsPerPage < 10) {
        throw new InvalidRequestItemsPerPageMinValueError(
          10,
          `Attribute itemsPerPage has value ${itemsPerPage}. Min value is 10.`
        );
      }
    }
    private static validateSearchQuery = (searchQuery: string): void => {
      if(searchQuery.trim().length === 0) {
        throw new InvalidRequestSearchQueryNonEmptyError(
          `Attribute searchQuery cannot be an empty string.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetEmailTemplatesRPC(pageIndex='${this.pageIndex}',itemsPerPage='${this.itemsPerPage}',sortOrder='${this.sortOrder}',sortColumn='${this.sortColumn}',searchQuery='${this.searchQuery}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    templates: EmailTemplateDTO[];
    totalItems: number;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetEmailTemplatesResponse: boolean = true;
  
    readonly templates: EmailTemplate[];
    readonly totalItems: number;
  
    static readonly constraints = {
      "totalItems": {
        "minValue": 0,
      }
    }
  
    constructor(
    templates: EmailTemplate[], 
    totalItems: number
    ) {
      this.templates = templates;
      Response.validateTotalItems(totalItems);
      this.totalItems = totalItems;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const templates = getList<EmailTemplateDTO, EmailTemplate>(
        dto,
        "templates",
        EmailTemplate.fromDTO,
        InvalidResponseTemplatesError
      );
      const totalItems = getInt32(dto, "totalItems", InvalidResponseTotalItemsError);
      return new Response(
        templates,
        totalItems
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          templates: this.templates.map((e: EmailTemplate) => { return e.toDTO() }),
          totalItems: this.totalItems
      };
      return dto;
    }
    private static validateTotalItems = (totalItems: number): void => {
      if(totalItems < 0) {
        throw new InvalidResponseTotalItemsMinValueError(
          0,
          `Attribute totalItems has value ${totalItems}. Min value is 0.`
        );
      }
    }
    
  }
  
  Response.prototype.toString = function toString(): string {
    return `GetEmailTemplates(templates='${this.templates}',totalItems='${this.totalItems}')`;
  };
  

  export enum RPCError {
    InvalidPageIndex = "INVALID_PAGE_INDEX"
  }

  export namespace Errors {
    
    export interface InvalidPageIndexDTO {
      code: string;
    }
    
    export class InvalidPageIndex extends LeoRPCError<RPCError> {
      readonly isInvalidPageIndex: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidPageIndex;
        super(code);
      }
    
    
      toDTO(): InvalidPageIndexDTO {
        const dto: InvalidPageIndexDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidPageIndex.prototype.toString = function toString(): string {
      return `GetEmailTemplatesRPC.INVALID_PAGE_INDEX()`;
    };
    

    export type Errors = InvalidPageIndex;
    export type ErrorsDTO = InvalidPageIndexDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidPageIndex:
          return new InvalidPageIndex();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestPageIndexError extends GetEmailTemplatesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexError";
  }
}

export class InvalidRequestPageIndexMinValueError extends InvalidRequestPageIndexError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPageIndexMinValueError";
  }
}

export class InvalidRequestItemsPerPageError extends GetEmailTemplatesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageError";
  }
}

export class InvalidRequestItemsPerPageMinValueError extends InvalidRequestItemsPerPageError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestItemsPerPageMinValueError";
  }
}

export class InvalidRequestSortOrderError extends GetEmailTemplatesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortOrderError";
  }
}

export class InvalidRequestSortColumnError extends GetEmailTemplatesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnError";
  }
}

export class InvalidRequestSortColumnTemplateNameError extends InvalidRequestSortColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnTemplateNameError";
  }
}

export class InvalidRequestSortColumnModifiedAtError extends InvalidRequestSortColumnError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSortColumnModifiedAtError";
  }
}

export class InvalidRequestSearchQueryError extends GetEmailTemplatesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSearchQueryError";
  }
}

export class InvalidRequestSearchQueryNonEmptyError extends InvalidRequestSearchQueryError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSearchQueryNonEmptyError";
  }
}

export class InvalidResponseTemplatesError extends GetEmailTemplatesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTemplatesError";
  }
}

export class InvalidResponseTotalItemsError extends GetEmailTemplatesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsError";
  }
}

export class InvalidResponseTotalItemsMinValueError extends InvalidResponseTotalItemsError {
  readonly minValue: number;
  constructor(minValue: number, message?: string) {
    super(message);
    this.minValue = minValue;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalItemsMinValueError";
  }
}

export class InvalidResponseInvalidPageIndexError extends GetEmailTemplatesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidPageIndexError";
  }
}

